import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';

import { cn, useDeepMemo } from '@crossnokaye/ux-common';

import type { TableColumnConfig } from '@repo/sdk/models/components';

import { type CellRenderContext, DataTable } from '@/components/data-table';
import { DIRTY_HIGHLIGHT_BORDER_CLASS } from '@/constants/cell-highlight';
import { useEquipmentBulkEditAccess } from '@/features/equipment-browser/access/use-equipment-bulk-edit-access';
import { pushOutModeAtom } from '@/features/equipment-browser/equipment-bulk-edit/state/bulk-edit-params.state';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { EquipmentBulkEditTableCell } from './cells/equipment-bulk-edit-table-cell';
import { DEVICE_NAME_COLUMN_ID, useBulkEditTableSort } from './use-bulk-edit-table-sort';
import { useDirtyCellTargets } from './use-dirty-cell-targets';
import { useEquipmentBulkEditTable } from './use-equipment-bulk-edit-table';
import {
  getDeviceDisabledTooltip,
  isBulkEditSettingCellDisabled,
  isDeviceFaultedOrEStopped,
} from '../equipment-bulk-edit.helpers';
import type {
  EquipmentBulkEditTableCellContent,
  EquipmentBulkEditTableData,
  EquipmentBulkEditTableProps,
} from '../equipment-bulk-edit.types';

export const EquipmentBulkEditTable = ({
  focusedColumnId,
  table,
  selectedDeviceIds,
  onSelectedDeviceIdsChange,
  selectedColumnIds,
}: EquipmentBulkEditTableProps) => {
  const { data } = useEquipmentBulkEditTable(table);
  const pushOutMode = useAtomValue(pushOutModeAtom);
  const isPushOutOpen = pushOutMode !== null;
  const { canUseMultiSelectEdit, multiSelectDeniedTooltip } = useEquipmentBulkEditAccess();
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({
      privilege: PRIVILEGES.writeCommands,
    });

  const isBulkEditPushOutOpen = isPushOutOpen && pushOutMode === 'edit';

  const tableId = `${table.id}-bulk-edit-table`;
  const dirtyCellTargets = useDirtyCellTargets();
  const hasFocusedSelectedColumn =
    focusedColumnId !== null &&
    focusedColumnId !== undefined &&
    (selectedColumnIds ?? []).includes(focusedColumnId);

  const columns = useDeepMemo(() => table.columns, [table.columns]);

  const { sorting, onSortingChange } = useBulkEditTableSort({
    isEditDrawerOpen: isBulkEditPushOutOpen,
  });

  const selection = useMemo<RowSelectionState>(() => {
    if (!selectedDeviceIds) return {};
    return selectedDeviceIds.reduce<RowSelectionState>((acc, id) => ({ ...acc, [id]: true }), {});
  }, [selectedDeviceIds]);

  const disabledSettingRowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of data) {
      if (isBulkEditSettingCellDisabled({ row, isPushOutOpen, disabledByPermissions })) {
        const rowId = String(row.deviceId?.value ?? '');
        if (rowId) {
          ids.add(rowId);
        }
      }
    }
    return ids;
  }, [data, disabledByPermissions, isPushOutOpen]);

  const iconColumnIds = useMemo(
    () => new Set(columns.filter((column) => column.showIcon).map((column) => column.columnId)),
    [columns]
  );

  const isSettingDisabledForRow = useCallback(
    (row: EquipmentBulkEditTableData): boolean => {
      const rowId = String(row.deviceId?.value ?? '');
      return disabledSettingRowIds.has(rowId);
    },
    [disabledSettingRowIds]
  );

  const getCellHoverClassName = useCallback(
    (
      context: CellRenderContext<EquipmentBulkEditTableData, EquipmentBulkEditTableCellContent>
    ): string | undefined => {
      const isIconCell = iconColumnIds.has(context.column.id) && Boolean(context.value?.deviceId);
      if (isIconCell) {
        return undefined;
      }

      return !isSettingDisabledForRow(context.row.original)
        ? 'hover:bg-ckds-neutral-800'
        : undefined;
    },
    [iconColumnIds, isSettingDisabledForRow]
  );

  const columnOverlayClassName = useCallback(
    (columnId: string): string =>
      hasFocusedSelectedColumn && columnId !== focusedColumnId
        ? 'border-2 border-solid border-ckds-neutral-400'
        : 'border-2 border-solid border-white',
    [hasFocusedSelectedColumn, focusedColumnId]
  );

  const columnOverlayHeaderClassName = useCallback(
    (columnId: string): string => {
      if (!hasFocusedSelectedColumn) {
        return 'border-l-2 border-r-2 border-t-2 border-l-white border-r-white border-t-white';
      }

      if (columnId === focusedColumnId) {
        return 'bg-ckds-base-white text-muted border-l-2 border-r-2 border-t-2 border-l-white border-r-white border-t-white';
      }

      return 'border-l-2 border-r-2 border-t-2 border-l-ckds-neutral-400 border-r-ckds-neutral-400 border-t-ckds-neutral-400';
    },
    [hasFocusedSelectedColumn, focusedColumnId]
  );

  const handleSelectionChange = (newSelection: RowSelectionState) => {
    const deviceIds = Object.keys(newSelection).filter((id) => newSelection[id]);
    onSelectedDeviceIdsChange(deviceIds);
  };

  return (
    <DataTable<EquipmentBulkEditTableData>
      data={data}
      id={tableId}
      width="full"
      getRowId={(row) => (row?.deviceId?.value as string) ?? ''}
    >
      <DataTable.Sorting value={sorting} onChange={onSortingChange} />
      <DataTable.ScrollTo columnId={focusedColumnId} align="start" />

      <DataTable.SelectColumn<EquipmentBulkEditTableData>
        disabledTooltip={(row) =>
          getDeviceDisabledTooltip(row) ||
          (!canUseMultiSelectEdit ? multiSelectDeniedTooltip : permissionTooltipText)
        }
        enableRowSelection={(row) =>
          !isPushOutOpen &&
          !isDeviceFaultedOrEStopped(row) &&
          !disabledByPermissions &&
          canUseMultiSelectEdit
        }
        selectAllMode={isBulkEditPushOutOpen ? 'none' : 'deselect-only'}
        onChange={handleSelectionChange}
        sortable
        value={selection}
      />

      {columns.map((column: TableColumnConfig, index: number) => {
        const isLastColumn = index === columns.length - 1;
        const pinned = column.columnId === DEVICE_NAME_COLUMN_ID ? 'left' : undefined;

        return (
          <DataTable.Column<EquipmentBulkEditTableData, EquipmentBulkEditTableData[string]>
            accessor={column.columnId}
            growFirst={isLastColumn}
            id={column.columnId}
            key={column.columnId}
            minWidth={100}
            pinned={pinned}
            resizable={!isLastColumn}
            sortable={column.canSort}
            sortingFn={(rowA, rowB, columnId) => {
              const a = (rowA.original[columnId]?.value ?? '').toLowerCase();
              const b = (rowB.original[columnId]?.value ?? '').toLowerCase();
              return a.localeCompare(b, undefined, { numeric: true });
            }}
            width={175}
          >
            <DataTable.Header>{column.label ?? ''}</DataTable.Header>
            <DataTable.Cell<EquipmentBulkEditTableData, EquipmentBulkEditTableData[string]>
              hoverClassName={getCellHoverClassName}
            >
              {(context) => {
                const isSettingDisabled = isSettingDisabledForRow(context.row.original);
                return (
                  <EquipmentBulkEditTableCell
                    context={context}
                    disabledByPermissions={disabledByPermissions}
                    isSettingDisabled={isSettingDisabled}
                    permissionTooltipText={permissionTooltipText}
                    showIcon={column.showIcon ?? false}
                  />
                );
              }}
            </DataTable.Cell>
          </DataTable.Column>
        );
      })}

      <DataTable.Settings>
        <DataTable.Density value="wide" />
        <DataTable.StickyHeader />
        <DataTable.DragScroll />
        <DataTable.Styles
          row={{
            base: cn(
              'bg-ckds-neutral-900 relative',
              isBulkEditPushOutOpen && [
                'text-ckds-text-fg-tier3',
                'after:content-[""] after:absolute after:inset-0 after:bg-black/30 after:pointer-events-none after:z-[1]',
              ]
            ),
            selected: cn(
              'bg-ckds-neutral-800 relative',
              isBulkEditPushOutOpen && ['text-ckds-text-fg-tier1', 'after:content-none']
            ),
          }}
        />
      </DataTable.Settings>

      <DataTable.ColumnOverlay
        className={columnOverlayClassName}
        columnIds={selectedColumnIds ?? []}
        headerClassName={columnOverlayHeaderClassName}
        rowCount={selectedDeviceIds?.length}
      />
      <DataTable.CellHighlight
        className={DIRTY_HIGHLIGHT_BORDER_CLASS}
        targets={dirtyCellTargets}
      />
    </DataTable>
  );
};
